import React, {useEffect, useRef} from 'react';
import styled from 'styled-components';
import {useChat} from '../context/ChatProvider';
import RatingStars from './RatingStars';

// Container for the whole conversation, keeps messages vertically aligned
const ConversationContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1vh;
    flex: 1;
    padding: 20px 0;
    overflow: auto;
`;

// Style for the message content container
const MessageContent = styled.div`
    display: flex;
    font-size: 0.8em;
    font-weight: 300;
    padding: 0.8em 1em;
    width: fit-content;
    height: fit-content;
    flex-direction: column;

    & h1, & h2, & h3, & h4, & h5, & h6 {
        margin: 10px 0;
        font-size: 1.2em;
    }

    & p {
        margin: 5px 0;
    }

    & ul, & ol {
        margin: 10px 0;
        padding-left: 20px;
    }

    & li {
        margin-bottom: 5px;
    }

    & strong {
        font-weight: bold;
    }

    & em {
        font-style: italic;
    }

    & a {
        font-weight: bold;
        color: var(--blue-button-color);
        text-decoration: none;
    }

    & a:hover {
        color: var(--blue-active-color);
    }
`;

// Style for user message container
const MessageContainerUser = styled.div`
    display: flex;
    gap: 15px;
    color: #fff;
    font-size: 1rem;
    justify-content: flex-start;
    align-items: flex-start;

    ${MessageContent} {
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.1);
        color: #000;
        box-shadow: rgba(0, 0, 0, 0.15) 2px 3px 15px;
        border-radius: 0 8px 8px 8px;
    }

    & img {
        height: 2vw;
        width: 2vw;
        min-height: 30px;
        min-width: 30px;
        border-radius: 50%;
        object-fit: cover;
    }
`;

// Style for bot message container
const MessageContainerBot = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    color: #fff;
    font-size: 1rem;
    align-items: flex-end;
    position: relative; /* Add this line to make it relative for absolute positioning of RatingStars */

    & > div {
        display: flex;
        gap: 15px;
        align-items: flex-start;
    }

    ${MessageContent} {
        background: #f5f5f5;
        border: 1px solid rgba(0, 0, 0, 0.1);
        color: #000;
        box-shadow: rgba(0, 0, 0, 0.1) 2px 3px 10px;
        border-radius: 8px 0 8px 8px;
        padding-bottom: 30px; /* Ensures there’s space for the stars at the bottom */
    }

    & img {
        height: 2vw;
        width: 2vw;
        min-height: 30px;
        min-width: 30px;
        border-radius: 50%;
        object-fit: cover;
    }
`;

// Style for system messages (e.g., notifications or status updates)
const SystemMessage = styled.div`
    width: fit-content;
    margin: 0 auto;
    padding: 0.85em 1.7em;
    font-size: 0.7em;
    text-align: center;
    border-radius: 2em;
    background: rgba(0, 0, 0, 0.05);
`;

// Styled component for loading spinner
const LoadingSpinner = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50px;
    width: 100%;

    &::after {
        content: "";
        display: block;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 5px solid #ccc;
        border-color: #ccc transparent #ccc transparent;
        animation: lds-dual-ring 1.2s linear infinite;
    }

    @keyframes lds-dual-ring {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

// Optional debug label style for displaying p_id
const DebugLabel = styled.small`
    display: block;
    font-size: 0.7em;
    color: #888;
    margin-top: 2px;
`;

const Conversation = () => {
    const {messagesByRoom, loading, currentRoom} = useChat();
    const chatConversation = useRef(null);

    // Retrieve the messages for the current room
    const roomMessages = currentRoom ? (messagesByRoom[currentRoom.id] || []) : [];

    useEffect(() => {
        if (chatConversation.current) {
            chatConversation.current.scrollTo(0, chatConversation.current.scrollHeight);
        }
    }, [roomMessages, loading]);

    // For debugging, log each message object
    useEffect(() => {
        console.log('Room messages:', roomMessages);
    }, [roomMessages]);

    return (
        <ConversationContainer ref={chatConversation}>
            {roomMessages.map((m) => {
                const {text, author, id, p_id} = m;

                // Debug: log the entire message object so you can inspect what comes in
                console.log('Message:', m);

                if (!text) {
                    console.error("Message text is missing:", m);
                    return null;
                }

                const isBot = author === 'BOT';

                if (author === 'SYSTEM') {
                    return (
                        <SystemMessage key={id}>
                            {text}
                        </SystemMessage>
                    );
                }

                return isBot ? (
                    <div key={id}>
                        <MessageContainerBot>
                            <div>
                                <MessageContent dangerouslySetInnerHTML={{__html: text}}/>

                            </div>
                        </MessageContainerBot>
                        <div>
                            <RatingStars pId={p_id} style={{position: 'absolute', bottom: '10px', left: '10px'}}/>
                        </div>
                    </div>
                ) : (
                    <MessageContainerUser key={id}>
                        <img src="/images/user-icon.webp" alt="user-logo"/>
                        <MessageContent dangerouslySetInnerHTML={{__html: text}}/>
                    </MessageContainerUser>
                );
            })}
            {loading && <LoadingSpinner/>}
        </ConversationContainer>
    );
};

export default Conversation;
